import type {
  AnnouncementErrorReason,
  AnnouncementTemplate,
  PreviousAnnouncementSend,
} from "../../client-messages/announcements";

/** Why the last attempt from the send dialog was refused. */
export interface AnnouncementSendFailure {
  readonly reason: AnnouncementErrorReason | undefined;
  readonly message: string;
  /** `alreadySent`: the send holding the 24-hour guard. */
  readonly previous?: PreviousAnnouncementSend | undefined;
}

/** What the page allows right now. Nothing goes out without a sendable template and a subject. */
export function announcementFormState(inputs: {
  template: AnnouncementTemplate | undefined;
  subject: string;
  listId: string | undefined;
}): { canTestSend: boolean; canSend: boolean } {
  const ready = inputs.template?.hasUnsubscribeTag === true && inputs.subject.trim() !== "";
  return { canTestSend: ready, canSend: ready && inputs.listId !== undefined };
}

/**
 * What a refusal lets the user do next. Only an "already sent" refusal offers
 * a resend, and not while that earlier send is still queueing (the server
 * refuses that too); a send that is in progress, stopped, or partially
 * queued must not be sent again from here.
 */
export function nextStep(failure: AnnouncementSendFailure | undefined): "resendAllowed" | "blocked" | "retry" {
  switch (failure?.reason) {
    case undefined:
      return "retry";
    case "alreadySent":
      // A resend must name the earlier send it replaces; without one there is nothing to offer.
      if (failure.previous === undefined) return "blocked";
      return failure.previous.status === "pending" && !failure.previous.stale ? "blocked" : "resendAllowed";
    case "inProgress":
    case "stalePending":
    case "partiallyQueued":
    case "notConfigured":
    case "sendGridNotConfigured":
    case "forbidden":
    case "templateRejected":
    case "listNotFound":
    case "listExpired":
    case "listExpiring":
    case "listTooLarge":
    case "replacedSendNotFound":
    case "emptyList":
      return "blocked";
    default:
      return "retry";
  }
}

/**
 * Whether "send it again anyway" is ticked for the refusal on screen. The
 * tick is recorded against the earlier send it was given for, so a later
 * refusal naming a different earlier send starts unticked.
 */
export function resendTicked(
  tickedForSendId: string | undefined,
  failure: AnnouncementSendFailure | undefined,
): boolean {
  return tickedForSendId !== undefined && tickedForSendId === failure?.previous?.sendId;
}
